import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-none border border-transparent bg-clip-padding text-[13px] font-medium whitespace-nowrap outline-none select-none transition-[background-color,border-color,color] duration-[var(--motion-fast)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[var(--signal)] disabled:pointer-events-none disabled:border-dashed disabled:border-[var(--rule)] disabled:bg-transparent disabled:text-[var(--ink-3)] disabled:opacity-100 aria-invalid:border-[var(--down)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "border-[var(--action)] bg-[var(--action)] text-[var(--on-action)] hover:border-[var(--action-hover)] hover:bg-[var(--action-hover)]",
        outline:
          "border-[var(--ink-1)] bg-transparent text-[var(--ink-1)] hover:bg-[var(--ground-sunk)] aria-expanded:bg-[var(--ground-sunk)]",
        secondary:
          "border-[var(--ink-1)] bg-transparent text-[var(--ink-1)] hover:bg-[var(--ground-sunk)]",
        ghost:
          "hover:bg-[var(--ground-sunk)] hover:text-foreground aria-expanded:bg-[var(--ground-sunk)]",
        destructive:
          "border-[var(--action)] bg-[var(--action)] text-[var(--on-action)] hover:border-[var(--action-hover)] hover:bg-[var(--action-hover)]",
        link: "text-[var(--ink-1)] underline-offset-4 hover:underline active:scale-100",
      },
      size: {
        default:
          "h-11 min-h-11 gap-2 px-4 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        xs: "h-6 gap-1 rounded-none px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-none px-2.5 text-[0.8rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-11",
        "icon-xs":
          "size-6 rounded-none [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-none",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
